/**
 * <s-announcement-bar> — rotates through `[data-announcement-slide]` children one at a time.
 *
 * Autoplay pauses on hover, keyboard focus, hidden tab, reduced motion, or the explicit
 * pause button; an explicit pause is sticky until the play button is pressed.
 * Inactive slides get aria-hidden + inert. The live region is off while rotating.
 */
class SAnnouncementBar extends HTMLElement {
  #ac;
  #timer = 0;
  #index = 0;
  #slides = [];
  #flags = { hover: false, focus: false, hidden: false, reduced: false, user: false };

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#slides = [...this.querySelectorAll('[data-announcement-slide]')];
    this.#index = 0;
    this.#show(0, false);

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#flags.reduced = motion.matches;
    motion.addEventListener('change', (event) => { this.#flags.reduced = event.matches; this.#update(); }, opts);

    this.addEventListener('pointerenter', () => { this.#flags.hover = true; this.#update(); }, opts);
    this.addEventListener('pointerleave', () => { this.#flags.hover = false; this.#update(); }, opts);
    this.addEventListener('focusin', () => { this.#flags.focus = true; this.#update(); }, opts);
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
      this.#flags.focus = false;
      this.#update();
    }, opts);
    document.addEventListener('visibilitychange', () => { this.#flags.hidden = document.hidden; this.#update(); }, opts);
    this.addEventListener('command', this.#onCommand, opts);

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
  }

  get #interval() {
    const seconds = Number(this.dataset.interval) || 5;
    return Math.max(3, seconds) * 1000;
  }

  /** @param {Event & { command?: string }} event */
  #onCommand = (event) => {
    if (event.command === '--next') this.#show(this.#index + 1, true);
    if (event.command === '--previous') this.#show(this.#index - 1, true);
    if (event.command === '--toggle') {
      this.#flags.user = !this.#flags.user;
      this.#update();
    }
  };

  #playing() {
    const f = this.#flags;
    return this.#slides.length > 1 && !f.hover && !f.focus && !f.hidden && !f.reduced && !f.user;
  }

  #update() {
    clearInterval(this.#timer);
    const playing = this.#playing();
    if (playing) this.#timer = window.setInterval(() => this.#show(this.#index + 1, false), this.#interval);

    const viewport = this.querySelector('[data-announcement-viewport]');
    viewport?.setAttribute('aria-live', playing ? 'off' : 'polite');

    const toggle = this.querySelector('[data-announcement-toggle]');
    if (toggle) {
      const paused = this.#flags.user;
      toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
      toggle.setAttribute('aria-label', paused ? toggle.dataset.playLabel ?? '' : toggle.dataset.pauseLabel ?? '');
      toggle.toggleAttribute('data-paused', paused);
    }
  }

  /**
   * @param {number} next
   * @param {boolean} manual
   */
  #show(next, manual) {
    const count = this.#slides.length;
    if (count === 0) return;
    this.#index = ((next % count) + count) % count;
    this.#slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    if (manual) this.#update();
  }
}

customElements.define('s-announcement-bar', SAnnouncementBar);
